import { AfterViewInit, Component, EventEmitter, Input, OnInit, Output, TemplateRef, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { Router } from '@angular/router';
import { forkJoin } from 'rxjs';
import { Role } from '../../../shared/models/user.model';
import { VoiceTestGroup } from '../../../shared/models/voice-testing.model';
import { ModalService } from '../../../shared/services/modal.service';
import { NotificationService } from '../../../shared/services/notification.service';
import { PricingService } from '../../../shared/services/pricing.service';
import { UsersService } from '../../../shared/services/users.service';
import { ValidationService, Validators as Vld } from '../../../shared/services/validation.service';
import { VoiceTestingService } from '../../../shared/services/voice-testing.service';
import { DestinationsSelectComponent } from '../../destinations-select/destinations-select.component';
import { SipConfigSelectComponent } from '../sip-config-select/sip-config-select.component';

@Component({
  standalone: false,
  selector: 'app-sip-form',
  templateUrl: 'sip-form.component.html',
  styleUrls: ['sip-form.component.scss'],
  inputs: ['model'],
  outputs: ['onAfterSave'],
})
export class SipFormComponent implements OnInit, AfterViewInit {
  @ViewChild(DestinationsSelectComponent, { static: false }) countries: DestinationsSelectComponent;
  @ViewChild(SipConfigSelectComponent, { static: false }) sipConfig: SipConfigSelectComponent;
  @ViewChild('confirmModalTpl', { read: TemplateRef, static: false }) confirmModalTpl: any;

  @Input() showButtons = true;
  @Input() autoRun = true;
  @Input() pauseUpdate = false;
  @Input() hiddenSearchButton = false;

  @Output() onChangeCountResults = new EventEmitter();
  @Output() onClickSearchButton = new EventEmitter();

  loading = true;
  backButtonShow = true;
  onAfterSave = new EventEmitter();
  countResults = 0;
  isAdmin = false;

  onAfterSaveEmmit = true;

  form: FormGroup;

  testsPrice = 0;
  ttl = 180;

  currentSection = '';

  constructor(
    public router: Router,
    public notificationService: NotificationService,
    private formBuilder: FormBuilder,
    public validationService: ValidationService,
    public service: VoiceTestingService,
    public modal: ModalService,
    public pricingService: PricingService,
    private userService: UsersService
  ) {}

  get ttlInput() {
    return this.form.get('ttl');
  }

  ngOnInit(): void {
    this.form = this.formBuilder.group({
      ttl: ['', Vld.compose([Vld.required, Vld.min(60), Vld.max(600)])],
      sipCallerId: ['', Vld.compose([Vld.globalPhoneNumber(true)])],
      countResults: ['', Vld.compose([Vld.min(1)])],
    });
    this.userService.getAuthUser().then(u => {
      this.isAdmin = u.role === Role.ADMIN;
    });
  }

  ngAfterViewInit() {
    forkJoin([this.sipConfig.update(true), this.countries.update()]).subscribe(() => {
      this.loading = false;
    });
    this.form.controls.sipCallerId.valueChanges.subscribe(() => {
      this.calculateCountResults();
    });
  }

  onSubmit() {
    this.loading = true;
    const dialogRef = this.modal.alert().component(this.confirmModalTpl).open();
    this.pricingService.getTestPrices(this.countResults, 'VOICE').subscribe({
      next: (result: number) => {
        this.testsPrice = result;
        this.loading = false;
        dialogRef.result.then(result => {
          if (result) {
            dialogRef.result.then(result => {
              if (result) {
                this.save();
              }
            });
          }
        });
      },
      complete: () => {
        this.loading = false;
      },
    });
  }

  private save() {
    this.loading = true;
    return this.service.run(this.createModel()).subscribe(
      _ => {
        this.loading = false;
        this.notificationService.success('Test case created', 'SIP CLI testing');
        if (this.onAfterSaveEmmit) {
          this.onAfterSave.emit();
          this.countries.reset();
        }
        this.calculateCountResults();
      },
      error => {
        this.loading = false;
        this.notificationService.error({
          title: 'SIP CLI testing',
          message: 'An error occurred while running the tests',
          serviceName: 'VTC',
          requestMessage: error.statusText,
          requestCode: error.status,
          ts: error.timestamp ? error.timestamp : null,
        });
      }
    );
  }

  calculateCountResults() {
    const sipCallerControl = this.form.controls.sipCallerId;
    const countResults =
      this.countries.getSelectedModels().length *
      this.sipConfig.getSelectedModels().length *
      (sipCallerControl.value && sipCallerControl.valid ? 1 : 0);
    if (countResults !== this.countResults) {
      this.countResults = countResults;
      this.form.controls.countResults.patchValue(this.countResults);
      this.onChangeCountResults.emit(this.countResults);
    }
  }

  clickSearchButton() {
    this.onClickSearchButton.emit();
  }

  reset() {
    this.countries.reset();
    this.calculateCountResults();
  }

  private createModel(): VoiceTestGroup {
    const testGroup: VoiceTestGroup = {
      testDtos: [],
      ttl: this.ttl,
    };
    const sipConfig = this.sipConfig.getSelectedModels();
    const sipCallerId = this.form.controls.sipCallerId.value;
    testGroup.testDtos = this.countries.getSelectedModels().map(_ => {
      if (_.custom) {
        return {
          sip: true,
          sipConfigId: sipConfig[0].id,
          sipCallerId: sipCallerId,
          finished: false,
          destinationDto: {
            countryName: _.countryName ? _.countryName : null,
            mcc: null,
            mnc: null,
            originalMnc: null,
            originalProviderName: null,
            providerName: null,
            phone: _.phonenumber,
            manualNumber: true,
          },
        };
      }
      return {
        sip: true,
        sipConfigId: sipConfig[0].id,
        sipCallerId: sipCallerId,
        finished: false,
        destinationDto: {
          countryName: _.countryName,
          mcc: _.mcc,
          mnc: _.mnc,
          originalMnc: _.originalMnc,
          originalProviderName: _.originalProviderName,
          providerName: _.providerName,
          manualNumber: false,
        },
      };
    });

    return testGroup;
  }
}
